<template>
  <div class="page">
    <BookingBack title="Gift Card Details" :textColor="'#333333'" :isCenter="true">
      <template #right>
        <div class="delete-inline" @click="remove">
          <span class="text">Delete Gift Card</span>
          <van-icon name="delete-o" color="#ff4d4f" size="16" />
        </div>
      </template>
    </BookingBack>
    <van-skeleton avatar title :row="6" :loading="loading">
      <div class="content" v-if="detail">
        <!-- Gift Card展示 -->
        <MyCard :card="card" :clickable="false" />
        <!-- Valid For 区域 -->
        <div class="info-section">
          <div class="section-title">Valid For:</div>
          <div class="section-subtitle">{{ scopeText }}</div>
          <div class="shop-info">
            <img class="shop-logo" :src="supplierLogo" alt="Merchant logo" />
            <div class="shop-details">
              <div class="shop-name">{{ supplierInfo.name || 'Merchant' }}</div>
              <div class="shop-address">{{ supplierInfo.address || 'No address' }}</div>
            </div>
          </div>
        </div>
        <!-- Price/Value/Balance/Expires 信息行 -->
        <div class="info-rows">
          <div class="info-row">
            <span class="info-label">Price</span>
            <span class="info-value">{{ formatCurrency(detail.price) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Value</span>
            <span class="info-value">{{ formatCurrency(detail.value) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Balance</span>
            <span class="info-value highlight">{{ formatCurrency(card.amount) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Expires After</span>
            <span class="info-value">{{ expirationText }}</span>
          </div>
        </div>
        <!-- 条款说明 -->
        <div class="info-terms">
          <p class="term-dark">Gift Cards Are Not Personalized And Can Be Used By Anyone With The Card Number</p>
          <p class="term-dark">Gift Cards Are Not Single Use And Can Be Used As Many Times As Necessary Until The Balance Is Fully Consumed</p>
        </div>
        <div class="info-terms-light">
          <p>After Purchasing The Card, Your Personal Data Will Be Processed By The Partner From Whom You Buy Card. You Will Find Detailed Information In Partner Information</p>
          <p>Detailed Rules For Using Gift Cards Can Be Found In The Terms And Conditions</p>
        </div>
      </div>
    </van-skeleton>
    <van-empty v-if="!loading && !detail" description="No gift card details" />
    <OrderBtn>
      <div class="copy-btn" @click="copy">Copy Card Number</div>
    </OrderBtn>
    <div class="share-tip">Please share the card number manually</div>
  </div>
</template>
<script>
import BookingBack from '@/components/Booking/back.vue'
import OrderBtn from '@/components/Order/btn.vue'
import MyCard from '@/components/GiftCard/MyCard.vue'
import { Dialog, Toast } from 'vant'
export default {
  name: 'MyGiftCardDetail',
  components: { BookingBack, OrderBtn, MyCard },
  data() {
    return {
      card: {
        id: '',
        number: '',
        storeName: 'Gift Card',
        address: '',
        amount: 0,
        bg: 1
      },
      detail: null,
      loading: false,
      // 绑定记录ID，用于解绑接口调用
      bindingId: ''
    }
  },
  computed: {
    supplierInfo() {
      return (this.detail && this.detail.supplier) || {}
    },
    supplierLogo() {
      const logo = this.supplierInfo.logo && this.supplierInfo.logo.file_path
      return this.formatImagePath(logo) || 'https://pic.rmb.bdstatic.com/bjh/bc11504608c0/250314/7904a9d0b5755cec95a4bb00d8763f94.png'
    },
    scopeText() {
      if (!this.detail) return '--'
      const scope = Number(this.detail.applicable_scope)
      if (scope === 0) return 'All Services & Products'
      if (scope === 1) return 'Selected Services Only'
      return 'See Merchant Details'
    },
    expirationText() {
      if (!this.detail) return '--'
      const days = Number(this.detail.expiration_days)
      if (!days) return 'No Expiration'
      return `${days} Days From Purchase`
    }
  },
  created() {
    this.initPage()
  },
  methods: {
    initPage() {
      const { id, card_no, bg, binding_id, balance } = this.$route.query
      if (card_no) {
        this.card.number = card_no
      }
      if (bg) {
        const parsed = Number(bg)
        this.card.bg = Number.isNaN(parsed) ? this.randomBg() : parsed
      } else {
        this.card.bg = this.randomBg()
      }
      // 从列表传递过来的余额
      if (balance !== undefined && balance !== null && balance !== '') {
        this.card.amount = Number(balance) || 0
      }
      if (id) {
        this.card.id = id
        this.getCardDetail(id)
      }
      // 记录绑定ID
      if (binding_id) {
        this.bindingId = binding_id
      }
    },
    async getCardDetail(id) {
      this.loading = true
      try {
        const res = await this.$api.getCardDetail({ id })
        if (res.code === 1 && res.data && res.data.detail) {
          this.detail = res.data.detail
          this.syncCardInfo(this.detail)
        }
      } catch (error) {
        // ignore
      } finally {
        this.loading = false
      }
    },
    syncCardInfo(detail) {
      const supplier = detail.supplier || {}
      // 保留从列表传递过来的余额，如果没有则使用详情中的value或price
      const currentAmount = this.card.amount
      this.card = {
        ...this.card,
        storeName: detail.name || supplier.name || this.card.storeName,
        address: supplier.address || this.card.address,
        amount: currentAmount || detail.value || detail.price || 0,
        card_image: detail.card_image || '' // 使用接口返回的 card_image
      }
    },
    formatCurrency(value) {
      const num = Number(value || 0)
      return `$${num.toFixed(2)}`
    },
    formatImagePath(path) {
      if (!path) return ''
      let finalPath = path.trim()
      if (/^https?:/i.test(finalPath)) {
        return finalPath.replace('http://localhost:8080', 'http://www.hwm.red')
      }
      finalPath = finalPath.replace(/^\/+/, '')
      // 相对路径需要加上 /uploads/ 前缀（参考网页端的 resolveUploadUrl）
      const fullPath = finalPath.startsWith('uploads/') ? `/${finalPath}` : `/uploads/${finalPath}`
      return `http://www.hwm.red${fullPath}`
    },
    randomBg() {
      return Math.floor(Math.random() * 3) + 1
    },
    copy() {
      const text = this.card.number
      if (!text) {
        Toast && Toast('No card number')
        return
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          Toast && Toast('Card number copied')
        })
      } else {
        const input = document.createElement('input')
        input.value = text
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        Toast && Toast('Card number copied')
      }
    },
    remove() {
      Dialog.confirm({ title: 'Delete Gift Card', message: 'Are you sure you want to delete this gift card?' })
        .then(() => {
          if (this.bindingId) {
            this.handleUnbind()
          } else {
            this.$router.go(-1)
          }
        })
        .catch(() => {})
    },
    async handleUnbind() {
      const id = this.bindingId
      if (!id) {
        Toast && Toast('Missing binding ID. Unable to delete.')
        return
      }
      try {
        const res = await this.$api.unbindGiftCard({ binding_id: id })
        const msg = (res && res.msg) || ''
        if (msg) {
          Toast && Toast(msg)
        }
        if (res && res.code === 1) {
          // 解绑成功后稍作停顿再返回“My 礼品卡s”列表，确保提示能被看到
          setTimeout(() => {
            this.$router.replace({ path: '/myGiftCard' })
          }, 600)
        }
      } catch (error) {
        Toast && Toast('Unbind failed. Please try again later.')
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.delete-inline {
  display: flex;
  align-items: center;
  gap: 4px;
  .text {
    color: #ff4d4f;
    font-size: 0.875rem;
  }
}
.content {
  padding: 1rem;
  padding-top: 1.5rem;
  box-sizing: border-box;
}
// Valid For 区域
.info-section {
  padding: 1rem 0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 0;
  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
  }
  .section-subtitle {
    font-size: 0.8125rem;
    color: #666;
    margin-bottom: 1rem;
  }
  .shop-info {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    .shop-logo {
      width: 2.75rem;
      height: 2.75rem;
      border-radius: 50%;
      overflow: hidden;
      flex-shrink: 0;
      background: #f0f0f0;
      object-fit: cover;
    }
    .shop-details {
      flex: 1;
      .shop-name {
        font-size: 0.875rem;
        font-weight: 600;
        color: #333;
        margin-bottom: 2px;
      }
      .shop-address {
        font-size: 0.75rem;
        color: #999;
        line-height: 1.4;
      }
    }
  }
}
// Price/Value/Expires 信息行
.info-rows {
  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f0f0f0;
    .info-label {
      font-size: 0.875rem;
      color: #333;
    }
    .info-value {
      font-size: 0.875rem;
      font-weight: 600;
      color: #333;
      &.highlight {
        color: #1a3a2f;
      }
    }
  }
}
// 条款说明（深色）
.info-terms {
  margin-top: 1rem;
  .term-dark {
    font-size: 0.75rem;
    color: #333;
    line-height: 1.6;
    margin-bottom: 0.75rem;
    &:last-child {
      margin-bottom: 0;
    }
  }
}
// 条款说明（浅色）
.info-terms-light {
  margin-top: 1rem;
  margin-bottom: 1rem;
  p {
    font-size: 0.75rem;
    color: #999;
    line-height: 1.6;
    margin-bottom: 0.75rem;
    &:last-child {
      margin-bottom: 0;
    }
  }
}
.copy-btn {
  width: 100%;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background: #1a3a2f;
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 600;
}
.share-tip {
  text-align: center;
  color: #999;
  font-size: 0.75rem;
  margin: 0.5rem 0 1rem;
}
</style>
